import React, { useState, useEffect } from "react";
import { Typography, Box, Container } from "@mui/material";
import { styled } from "styled-components";
import PageWrapper from "../components/PageWrapper";
import { checkForUpdates } from "../utils/jsonUtils";

import { FaSpotify } from "react-icons/fa";
import AvisGoogle from "../components/AvisGoogle";
import { settings } from "../data/settings";
import theme from "../theme";
import useIsPortrait from "../components/hooks/useIsPortrait";
import SEO from "../components/SEO";

const smBreak = "730px";
// Styled Components
const FullScreenImage = styled.div`
  width: 100%;
  height: ${({ navBarHeight }) => `calc(100vh - ${navBarHeight})` || "100%"};
  overflow: hidden; /* Évite le débordement */
  position: relative;
  display: flex;

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover; /* Assure un recouvrement parfait */
    position: absolute;
    top: 0;
    left: 0;
    filter: brightness(0.9) grayscale(100%);
  }

  & div {
    z-index: 1;
    color: white;
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
    flex-direction: column;
    margin-bottom: -20vh;

    ${theme.breakpoints.down("sm")} {
      justify-content: flex-end;
      margin-bottom: 5rem;
      margin: 0rem 0.25rem;
      margin-bottom: 10vh;
    }

    & img {
      max-height: 25vh !important;
      position: relative !important;
      margin-bottom: 0rem;
      width: auto;
      object-fit: cover;
    }
    & div {
      font-size: 5rem;
      font-weight: 900;
      z-index: 1;
      line-height: 5rem;
      text-align: center;

      ${theme.breakpoints.down("sm")} {
        font-size: 3rem;
        line-height: 3rem;
      }
    }
  }
`;

const BandeauNoir = styled.div`
  background: black;
  padding: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;

  ${theme.breakpoints.down("sm")} {
    padding: 1rem;
  }
`;

const BandeauContent = styled.div`
  max-width: ${({ maxWidth }) => maxWidth || "100%"};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: space-between;

  ${theme.breakpoints.down("sm")} {
    justify-content: center;
    flex-direction: column;
  }

  & img {
    max-width: calc(10rem + 3rem);
    object-fit: cover;
    filter: grayscale(100%);
    aspect-ratio: 1;
  }
`;

const StyledTextSection = styled(Box)`
  text-align: ${({ align }) => align || "left"};
  max-width: ${({ maxWidth }) => maxWidth || "100%"};
  margin: auto;
  margin: 2rem auto;
  padding: 1rem;
  ${theme.breakpoints.down("sm")} {
    margin: 0.5rem;
    padding: 0rem;
  }
`;

const TitleSection = styled.div`
  flex-direction: ${(props) =>
    props.align === "right" ? "row" : "row-reverse"};
  display: flex;
  margin-bottom: 1.5rem;
  ${theme.breakpoints.down("sm")} {
    flex-direction: column;
  }
`;
const TitleImage = styled.div`
  display: flex;
  height: 8rem;
  width: 8rem;
  ${theme.breakpoints.down("sm")} {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
    margin-bottom: 1rem;
  }
  & img {
    background-color: black;
    height: 8rem;
    width: 8rem;
    object-fit: cover;
    filter: grayscale(100%);
    ${theme.breakpoints.down("sm")} {
      width: 100%;
      height: 100%;
      margin: auto;
      aspect-ratio: 1;
    }
  }
`;

const TitleText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(props) =>
    props.align === "right" ? "flex-end" : "flex-start"};
  padding: 0rem 1rem 1rem 1rem;
  ${theme.breakpoints.down("sm")} {
    align-items: flex-end;
    padding: 0rem;
  }
`;
const Title = styled.h1`
  margin: 0;
  padding: 0;
  font-size: 2.2rem;
  font-weight: 800 !important;
  text-transform: uppercase;
  ${theme.breakpoints.down("sm")} {
    font-size: 1.8rem;
  }
`;

const SubTitle = styled.h2`
  margin: 0;
  padding: 0;
  font-size: 1.8rem;
  font-weight: 500;
  /*margin-top: -0.5rem;*/
  ${theme.breakpoints.down("sm")} {
    font-size: 1.4rem;
  }
`;
const TextSection = styled.div``;
const TextEndSection = styled.div`
  text-align: center;
  font-size: 1.2rem;
  margin: 1rem 0rem;
  font-weight: 700;
  ${theme.breakpoints.down("sm")} {
    font-size: 1.1rem;
  }
`;

const AmbianceSection = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  max-width: ${({ maxWidth }) =>
    "calc(" + maxWidth + " - 8rem)" || "100%"} !important;
  width: 100%;
  background: black;
  margin: auto;

  overflow: hidden;
  flex-direction: row;

  ${theme.breakpoints.down("sm")} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const AmbianceTextSection = styled.div`
  padding: 2rem;
  padding-left: 0rem;
  max-width: ${({ maxWidth }) => maxWidth || "100%"};
  width: 100%;
  flex: 1;
  & svg {
    height: 2.5rem;
    width: 2.5rem;
    color: white;
    margin-right: 1rem;
    position: absolute;
    width: 100%;
    height: 72%;
    left: 16rem;
    top: 2rem;
    opacity: 0.2;
    z-index: 0;

    ${theme.breakpoints.down("sm")} {
      left: 8rem;
    }
  }

  ${theme.breakpoints.down("sm")} {
    padding-left: 2rem;
    width: unset;
  }
`;

const AmbianceFrameSection = styled.div`
  border-radius: 0px;
  display: flex;
  z-index: 1;

  & iframe {
    width: 100%;
    margin: 2rem;
  }

  ${theme.breakpoints.down("sm")} {
    max-width: 100%;
    flex: unset;
  }
`;

const AmbianceTitleSection = styled.div`
  display: flex;
  margin-bottom: 1rem;
  align-items: center;
  justify-content: flex-start;
`;
const AmbianceTitle = styled.div`
  font-size: 1.8rem;
  font-weight: 800;
  text-transform: uppercase;
  color: white;
`;
const AmbianceTexte = styled.div`
  font-size: 1rem;
  font-weight: normal;
  color: white;
`;

const AvisWidget = styled.div`
  max-width: ${({ maxWidth }) => maxWidth || "100%"};
  display: flex;
  padding: 2rem 0rem;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: auto;
`;

const AvisTitle = styled.div`
  font-size: 2.2rem;
  font-weight: 800;
  text-transform: uppercase;
`;

const PageSalon = ({ pageKey }) => {
  const [content, setContent] = useState(null);
  const [contentMap, setContentMap] = useState(null);

  const isPortrait = useIsPortrait();

  useEffect(() => {
    checkForUpdates(pageKey, setContent);
  }, [pageKey]);

  useEffect(() => {
    if (content) {
      console.log("content", content); // Transforme les données en un objet indexé par 'id'
      const elementsById = content?.elements.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
      setContentMap(elementsById);
    } else {
      setContentMap(null);
    }
  }, [content]);

  if (!content) return <div>Chargement...</div>;
  return (
    <PageWrapper>
      <SEO />
      {contentMap && (
        <>
          {/* Image Plein Écran */}
          <FullScreenImage navBarHeight={settings.navbar.height}>
            <picture>
              <img
                src={
                  isPortrait
                    ? contentMap?.["img-main-bg-mobile"]?.content ||
                      contentMap?.["img-main-bg"]?.content
                    : contentMap?.["img-main-bg"]?.content
                }
                alt="main-bkg"
              />{" "}
            </picture>

            <div>
              {/*<img src={contentMap?.["img-main-logo"]?.content} alt="main-logo" />{" "}*/}
              <div
                dangerouslySetInnerHTML={{
                  __html: contentMap?.["txt-main-title"]?.content || "",
                }}
              />
            </div>
          </FullScreenImage>

          {/* Zone Texte avec Titre */}
          <StyledTextSection maxWidth={settings.pages.pageMaxWidth}>
            <TitleSection align="right">
              <TitleImage>
                <img
                  src={contentMap?.["img-hist-title"]?.content}
                  alt="histoire-img"
                />{" "}
              </TitleImage>

              <TitleText>
                <Title
                  dangerouslySetInnerHTML={{
                    __html: contentMap?.["txt-hist-title"]?.content || "",
                  }}
                ></Title>
                <SubTitle
                  dangerouslySetInnerHTML={{
                    __html: contentMap?.["txt-hist-subTitle"]?.content || "",
                  }}
                ></SubTitle>
              </TitleText>
            </TitleSection>
            <TextSection
              dangerouslySetInnerHTML={{
                __html: contentMap?.["txt-hist-text"]?.content || "",
              }}
            ></TextSection>
            <TextEndSection
              dangerouslySetInnerHTML={{
                __html: contentMap?.["txt-hist-text-end"]?.content || "",
              }}
            ></TextEndSection>
          </StyledTextSection>

          {/* Bandeau Noir avec Images */}
          <BandeauNoir>
            <BandeauContent maxWidth={settings.pages.pageMaxWidth}>
              <img
                src={contentMap?.["img-bandeau1"]?.content}
                alt="bandeau-1"
              />{" "}
              <img
                src={contentMap?.["img-bandeau2"]?.content}
                alt="bandeau-2"
              />{" "}
              <img
                src={contentMap?.["img-bandeau3"]?.content}
                alt="bandeau-3"
              />
            </BandeauContent>
          </BandeauNoir>

          {/* Texte avec Titre Aligné Droite */}
          <StyledTextSection maxWidth={settings.pages.pageMaxWidth}>
            <TitleSection align="left">
              <TitleImage>
                <img
                  src={contentMap?.["img-adn-title"]?.content}
                  alt="adn-img"
                />{" "}
              </TitleImage>

              <TitleText align="right">
                <Title
                  dangerouslySetInnerHTML={{
                    __html: contentMap?.["txt-adn-title"]?.content || "",
                  }}
                ></Title>
                <SubTitle
                  dangerouslySetInnerHTML={{
                    __html: contentMap?.["txt-adn-subTitle"]?.content || "",
                  }}
                ></SubTitle>
              </TitleText>
            </TitleSection>
            <TextSection
              dangerouslySetInnerHTML={{
                __html: contentMap?.["txt-adn-text"]?.content || "",
              }}
            ></TextSection>
            <TextEndSection
              dangerouslySetInnerHTML={{
                __html: contentMap?.["txt-adn-text-end"]?.content || "",
              }}
            ></TextEndSection>
            <img
              style={{
                margin: "auto",
                display: "block",
                width: "2rem",
                height: "auto",
                aspectRatio: "1",
                marginTop: "-1rem",
              }}
              src={contentMap?.["img-adn-text-end"]?.content || ""}
              alt="adn-img"
            />
          </StyledTextSection>

          {/* Section Ambiance du Salon */}
          <AmbianceSection maxWidth={settings.pages.pageMaxWidth}>
            <AmbianceFrameSection>
              <iframe
                style={{ borderRadius: "0px" }}
                src={
                  "https://open.spotify.com/embed/" + settings.footer.spotify
                }
                //src="https://open.spotify.com/embed/playlist/4ip1BJvafkHHxIOFdUS6ZL?utm_source=generator&theme=0"
                width="100%"
                height="152"
                frameBorder="0"
                allowfullscreen=""
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              ></iframe>
            </AmbianceFrameSection>

            <AmbianceTextSection maxWidth={settings.pages.pageMaxWidth}>
              <AmbianceTitleSection>
                <FaSpotify color={"white"} />
                <AmbianceTitle
                  dangerouslySetInnerHTML={{
                    __html: contentMap?.["txt-ambiance-title"]?.content || "",
                  }}
                />
              </AmbianceTitleSection>
              <AmbianceTexte
                dangerouslySetInnerHTML={{
                  __html: contentMap?.["txt-ambiance-texte"]?.content || "",
                }}
              />
            </AmbianceTextSection>
          </AmbianceSection>

          {/* Avis Google Défilant */}
          <AvisWidget
            maxWidth={"calc( " + settings.pages.pageMaxWidth + " + 10rem)"}
          >
            <AvisTitle
              dangerouslySetInnerHTML={{
                __html: contentMap?.["txt-avis-title"]?.content || "",
              }}
            />
            <AvisGoogle />
          </AvisWidget>
        </>
      )}
    </PageWrapper>
  );
};

export default PageSalon;
